import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Picto, { availablePictos } from '../../atoms/Picto/Picto';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';
import cn from '../../../utils/cn';
import styles from './CalendarEventFocus.module.css';

/**
 * @param {string | null | undefined} icon
 * @param {keyof typeof availablePictos} fallback
 * @returns {keyof typeof availablePictos}
 */
function resolvePictoIcon(icon, fallback) {
  if (icon && Object.prototype.hasOwnProperty.call(availablePictos, icon)) {
    return /** @type {keyof typeof availablePictos} */ (icon);
  }
  return fallback;
}

/**
 * @param {object} props
 * @param {string | null} props.icon
 * @param {keyof typeof availablePictos} [props.fallback='calendar']
 * @returns {JSX.Element}
 */
function FocusPicto({ icon, fallback = 'calendar' }) {
  const iconDefinition = parseFontAwesomeClass(icon);
  if (iconDefinition) {
    return (
      <div className={styles.picto}>
        <FontAwesomeIcon
          icon={/** @type {any} */ (iconDefinition)}
          className={styles['picto-icon']}
        />
      </div>
    );
  }

  return (
    <div className={styles.picto}>
      <Picto icon={resolvePictoIcon(icon, fallback)} className={styles['picto-icon']} />
    </div>
  );
}

/**
 * @param {object} props
 * @param {EventFocusViewModel} props.viewModel
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function CalendarEventFocus({ viewModel, className }) {
  const { header, rows } = viewModel;

  return (
    <div className={cn([styles.card, className])}>
      <div className={styles.header}>
        <FocusPicto icon={header.icon} />
        <div className={styles['header-content']}>
          <MarkdownContainer
            content={header.title}
            className={styles['header-title']}
          />
          <p className={styles['header-date']}>{header.dateLabel}</p>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className={styles.rows}>
          {rows.map((row) => (
            <div
              key={row.type === 'text' ? `text-${row.icon}-${row.text}` : `list-${row.icon}-${row.label}`}
              className={cn([
                styles.row,
                row.type === 'list' && styles['row-list'],
              ])}
            >
              <FocusPicto icon={row.icon} fallback="info-circle" />
              {row.type === 'text' ? (
                <p className={styles['row-text']}>{row.text}</p>
              ) : (
                <div className={styles['list-content']}>
                  <p className={styles['list-label']}>{row.label}</p>
                  <ul className={styles['list-items']}>
                    {row.items.map((item) => (
                      <li key={item.title} className={styles['list-item']}>
                        <p className={styles['list-item-title']}>{item.title}</p>
                        {item.subtitle ? (
                          <p className={styles['list-item-subtitle']}>{item.subtitle}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default CalendarEventFocus;
